/**
 * Period Comparison Chart Component
 * Gráfico de barras comparando período atual vs anterior
 */

import React from 'react';
import { Card, CardContent, Typography, Box, CircularProgress, Alert, Chip } from '@mui/material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { TrendingUp, TrendingDown } from '@mui/icons-material';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

interface PeriodComparisonChartProps {
  data: {
    current: { total: number; successful: number; failed: number; uniqueUsers: number };
    previous: { total: number; successful: number; failed: number; uniqueUsers: number };
    changes: {
      total: number;
      successful: number;
      failed: number;
      uniqueUsers: number;
      successRate: number;
    };
  };
  loading?: boolean;
  error?: string | null;
  periodLabel?: string;
}

export const PeriodComparisonChart: React.FC<PeriodComparisonChartProps> = ({
  data,
  loading,
  error,
  periodLabel = '7 dias'
}) => {
  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent>
          <Alert severity="error">{error}</Alert>
        </CardContent>
      </Card>
    );
  }

  const chartData = {
    labels: ['Total de Ações', 'Ações Bem-sucedidas', 'Ações com Falha', 'Usuários Únicos'],
    datasets: [
      {
        label: `Período Anterior (${periodLabel})`,
        data: [
          data.previous.total,
          data.previous.successful,
          data.previous.failed,
          data.previous.uniqueUsers
        ],
        backgroundColor: 'rgba(201, 203, 207, 0.8)',
        borderColor: 'rgba(201, 203, 207, 1)',
        borderWidth: 1
      },
      {
        label: `Período Atual (${periodLabel})`,
        data: [
          data.current.total,
          data.current.successful,
          data.current.failed,
          data.current.uniqueUsers
        ],
        backgroundColor: 'rgba(54, 162, 235, 0.8)',
        borderColor: 'rgba(54, 162, 235, 1)',
        borderWidth: 1
      }
    ]
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const
      },
      tooltip: {
        callbacks: {
          afterLabel: function (context: any) {
            const index = context.dataIndex;
            const changes = [
              data.changes.total,
              data.changes.successful,
              data.changes.failed,
              data.changes.uniqueUsers
            ];
            const change = changes[index];
            if (change !== undefined && change !== 0) {
              return `Variação: ${change > 0 ? '+' : ''}${change}%`;
            }
            return 'Sem variação';
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Quantidade'
        }
      }
    }
  };

  const renderChangeChip = (value: number, label: string) => {
    const isPositive = value > 0;
    const isNegative = value < 0;
    const color = isPositive ? 'success' : isNegative ? 'error' : 'default';
    const icon = isPositive ? <TrendingUp /> : isNegative ? <TrendingDown /> : undefined;

    // Formatar variação de forma mais clara
    const formatVariation = (val: number): string => {
      if (val === 0) return 'Sem variação';
      if (val > 0) {
        return `+${val}% (aumento)`;
      }
      return `${val}% (redução)`;
    };

    return (
      <Chip
        {...(icon && { icon })}
        label={`${label}: ${formatVariation(value)}`}
        color={color}
        size="small"
        sx={{ mr: 1, mb: 1 }}
        title={`Variação percentual em relação ao período anterior`}
      />
    );
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Comparação de Períodos
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Compare o período atual com o anterior para identificar tendências e mudanças
          significativas.
          <br />
          <strong>Nota:</strong> Os percentuais abaixo representam{' '}
          <strong>variação percentual</strong> (crescimento/redução), não proporções. Valores acima
          de 100% indicam crescimento superior ao dobro do período anterior.
        </Typography>
        <Box sx={{ mb: 2 }}>
          {renderChangeChip(data.changes.total, 'Total')}
          {renderChangeChip(data.changes.successful, 'Sucessos')}
          {renderChangeChip(data.changes.failed, 'Falhas')}
          {renderChangeChip(data.changes.uniqueUsers, 'Usuários')}
          <Chip
            label={`Taxa de Sucesso: ${data.changes.successRate > 0 ? '+' : ''}${data.changes.successRate} pontos percentuais`}
            color={
              data.changes.successRate > 0
                ? 'success'
                : data.changes.successRate < 0
                  ? 'error'
                  : 'default'
            }
            size="small"
            sx={{ mr: 1, mb: 1 }}
          />
        </Box>
        <Box sx={{ height: 300 }}>
          <Bar data={chartData} options={options} />
        </Box>
      </CardContent>
    </Card>
  );
};
